// ═══════════════════════════════════════════════════════════════
// Admin — Users
// Passthrough UI to GAB user management.
// Lists app users, supports invite, and manages multi-role
// assignments directly via GAB API.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput,
  Modal, ActionIcon, Stack, Table, ScrollArea, Loader,
  Checkbox, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconTrash, IconUserPlus, IconUsers, IconShield,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import {
  listAppUsers, listRoles, getUserRoles,
  assignRole, removeRoleFromUser, inviteUser, removeUserFromApp,
} from '../../lib/gabAdmin';
import type { GabUser, GabRole } from '../../lib/gabAdmin';

interface UserWithRoles extends GabUser {
  roles: GabRole[];
  rolesLoaded: boolean;
}

export default function UsersTab() {
  const [users,        setUsers]        = useState<UserWithRoles[]>([]);
  const [allRoles,     setAllRoles]     = useState<GabRole[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');

  // Role assignment modal
  const [roleTarget,   setRoleTarget]   = useState<UserWithRoles | null>(null);
  const [savingRoles,  setSavingRoles]  = useState(false);
  const [pendingRoles, setPendingRoles] = useState<Set<string>>(new Set());

  // Invite modal
  const [inviteOpen,   setInviteOpen]   = useState(false);
  const [inviteEmail,  setInviteEmail]  = useState('');
  const [inviteFirst,  setInviteFirst]  = useState('');
  const [inviteLast,   setInviteLast]   = useState('');
  const [inviting,     setInviting]     = useState(false);

  // Remove confirm
  const [removeTarget, setRemoveTarget] = useState<UserWithRoles | null>(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rawUsers, roles] = await Promise.all([listAppUsers(), listRoles()]);
      setAllRoles(roles.filter(r => !r.isSystem));
      const withRoles: UserWithRoles[] = rawUsers.map(u => ({ ...u, roles: [], rolesLoaded: false }));
      setUsers(withRoles);
      // Load roles for each user in the background
      rawUsers.forEach(async u => {
        try {
          const roles = await getUserRoles(u.id);
          setUsers(prev => prev.map(p => p.id === u.id ? { ...p, roles, rolesLoaded: true } : p));
        } catch {
          setUsers(prev => prev.map(p => p.id === u.id ? { ...p, rolesLoaded: true } : p));
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const openRoleManager = (user: UserWithRoles) => {
    setRoleTarget(user);
    setPendingRoles(new Set(user.roles.map(r => r.id)));
  };

  const saveRoles = async () => {
    if (!roleTarget) return;
    setSavingRoles(true);
    const current = new Set(roleTarget.roles.map(r => r.id));
    const toAdd    = [...pendingRoles].filter(id => !current.has(id));
    const toRemove = [...current].filter(id => !pendingRoles.has(id));
    try {
      await Promise.all([
        ...toAdd.map(id    => assignRole(roleTarget.id, id)),
        ...toRemove.map(id => removeRoleFromUser(roleTarget.id, id)),
      ]);
      // Refresh this user's roles
      const updated = await getUserRoles(roleTarget.id);
      setUsers(prev => prev.map(u => u.id === roleTarget.id ? { ...u, roles: updated } : u));
      notifications.show({ message: 'Roles updated', color: 'var(--color-success)' });
      setRoleTarget(null);
    } catch {
      notifications.show({ message: 'Failed to update roles', color: 'var(--color-error)' });
    } finally { setSavingRoles(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { notifications.show({ message: 'Email required', color: 'var(--color-error)' }); return; }
    setInviting(true);
    try {
      await inviteUser(inviteEmail.trim(), inviteFirst.trim() || undefined, inviteLast.trim() || undefined);
      notifications.show({ message: `Invitation sent to ${inviteEmail}`, color: 'var(--color-success)' });
      setInviteOpen(false);
      setInviteEmail(''); setInviteFirst(''); setInviteLast('');
      loadAll();
    } catch {
      notifications.show({ message: 'Failed to send invitation', color: 'var(--color-error)' });
    } finally { setInviting(false); }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      await removeUserFromApp(removeTarget.id);
      notifications.show({ message: `${removeTarget.name ?? removeTarget.email} removed`, color: '#939598' });
      setRemoveTarget(null);
      setUsers(prev => prev.filter(u => u.id !== removeTarget.id));
    } catch {
      notifications.show({ message: 'Failed to remove user', color: 'var(--color-error)' });
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u =>
      u.email.toLowerCase().includes(q) ||
      (u.name ?? '').toLowerCase().includes(q)
    );
  }, [users, search]);

  // Group roles by functional area for the role picker
  const roleGroups = useMemo(() => {
    const groups: Record<string, GabRole[]> = {
      'System':             [],
      'EOC Command':        [],
      'EOC Sections':       [],
      'Emergency Management': [],
      'Debris':             [],
      'Shelter':            [],
      'Volunteer':          [],
      'Grants & Finance':   [],
    };
    for (const role of allRoles) {
      const n = role.name;
      if (n.includes('System') || n.includes('Auditor') || n.includes('Observer')) { groups['System'].push(role); }
      else if (n.includes('EOC') || n.includes('Incident') || n.includes('PIO') || n.includes('Safety') || n.includes('Liaison') || n.includes('Deputy Incident')) { groups['EOC Command'].push(role); }
      else if (n.includes('Section Chief')) { groups['EOC Sections'].push(role); }
      else if (n.includes('Emergency Manager') || n.includes('Deputy Emergency') || n.includes('Recovery') || n.includes('Mitigation')) { groups['Emergency Management'].push(role); }
      else if (n.includes('Debris')) { groups['Debris'].push(role); }
      else if (n.includes('Shelter')) { groups['Shelter'].push(role); }
      else if (n.includes('Volunteer') || n.includes('CERT')) { groups['Volunteer'].push(role); }
      else { groups['Grants & Finance'].push(role); }
    }
    return Object.entries(groups).filter(([, r]) => r.length > 0);
  }, [allRoles]);

  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Users</Title>
        <Button leftSection={<IconUserPlus size={14} />} size="xs" onClick={() => setInviteOpen(true)}>
          Invite User
        </Button>
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Users are managed at the GAB platform level. Inviting here adds them to this application.
        Multiple roles can be assigned to any user.
      </Text>

      <TextInput
        placeholder="Search by name or email..."
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={e => setSearch(e.target.value)}
        mb="sm"
        size="sm"
      />

      <Paper withBorder>
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Roles</Table.Th>
                <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && (
                <Table.Tr><Table.Td colSpan={4}><Group justify="center" py="md"><Loader size="sm" /></Group></Table.Td></Table.Tr>
              )}
              {!loading && filtered.length === 0 && (
                <Table.Tr><Table.Td colSpan={4}><Text c="dimmed" ta="center" py="lg">No users found</Text></Table.Td></Table.Tr>
              )}
              {filtered.map(user => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Group gap={6}>
                      <IconUsers size={14} color="#7B7D7F" />
                      <Text size="sm" fw={500}>{user.name ?? user.email}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{user.email}</Text></Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="wrap">
                      {!user.rolesLoaded && <Loader size={10} />}
                      {user.roles.length === 0 && user.rolesLoaded && (
                        <Text size="xs" c="dimmed">No roles</Text>
                      )}
                      {user.roles.map(r => (
                        <Badge key={r.id} size="xs" variant="light">{r.name}</Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Button size="xs" variant="subtle" leftSection={<IconShield size={12} />}
                        onClick={() => openRoleManager(user)}>
                        Roles
                      </Button>
                      <ActionIcon variant="subtle" color="var(--color-error)" size="sm" onClick={() => setRemoveTarget(user)}>
                        <IconTrash size={13} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* Role Assignment Modal */}
      <Modal
        opened={!!roleTarget}
        onClose={() => setRoleTarget(null)}
        title={`Manage Roles — ${roleTarget?.name ?? roleTarget?.email}`}
        size="md"
      >
        <Text size="xs" c="dimmed" mb="md">
          Select one or more roles. Permissions are the union of all assigned roles.
        </Text>
        <Stack gap="md">
          {roleGroups.map(([group, roles]) => (
            <div key={group}>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>{group}</Text>
              <Stack gap={4}>
                {roles.map(role => (
                  <Checkbox
                    key={role.id}
                    size="sm"
                    label={
                      <div>
                        <Text size="sm" fw={500}>{role.name}</Text>
                        {role.description && <Text size="xs" c="dimmed">{role.description}</Text>}
                      </div>
                    }
                    checked={pendingRoles.has(role.id)}
                    onChange={e => {
                      const checked = e.currentTarget.checked;
                      setPendingRoles(prev => {
                        const next = new Set(prev);
                        checked ? next.add(role.id) : next.delete(role.id);
                        return next;
                      });
                    }}
                  />
                ))}
              </Stack>
            </div>
          ))}
        </Stack>
        <Divider my="md" />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setRoleTarget(null)}>Cancel</Button>
          <Button loading={savingRoles} onClick={saveRoles}>Save Roles</Button>
        </Group>
      </Modal>

      {/* Invite Modal */}
      <Modal opened={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite User" size="sm">
        <Stack gap="sm">
          <TextInput label="Email" required value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
          <Group grow>
            <TextInput label="First Name" value={inviteFirst} onChange={e => setInviteFirst(e.target.value)} />
            <TextInput label="Last Name"  value={inviteLast}  onChange={e => setInviteLast(e.target.value)} />
          </Group>
          <Text size="xs" c="dimmed">
            An invitation email will be sent. The user will be added to this application on acceptance.
          </Text>
          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button loading={inviting} onClick={handleInvite}>Send Invitation</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Remove Confirm */}
      <Modal opened={!!removeTarget} onClose={() => setRemoveTarget(null)} title="Remove User" size="sm">
        <Text mb="md">
          Remove <strong>{removeTarget?.name ?? removeTarget?.email}</strong> from this application?
          Their GAB account will remain active.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button color="var(--color-error)" onClick={handleRemove}>Remove</Button>
        </Group>
      </Modal>
    </>
  );
}
